// /en/translate/$language/$doctype — 22 languages × 15 doctypes = 330 pages
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import {
  TRANSLATION_LANGUAGES,
  getTranslationLanguage,
} from "@/data/translation-languages";
import {
  TRANSLATION_DOCTYPES,
  getTranslationDocType,
} from "@/data/translation-doctypes";
import { enPairSections } from "@/data/en-translate-content";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, dynamicOgImage, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import {
  FileCheck2, ShieldCheck, BadgeCheck, Globe2, Phone, MessageCircle, Languages, ArrowRight,
} from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/translate/$language/$doctype/")({
  loader: ({ params }) => {
    if (!getTranslationLanguage(params.language) || !getTranslationDocType(params.doctype)) throw notFound();
    return { lang: params.language, doc: params.doctype };
  },
  head: ({ params }) => {
    const l = getTranslationLanguage(params.language);
    const d = getTranslationDocType(params.doctype);
    if (!l || !d) return { meta: [{ title: ctaTitle("Page not found", "/en/translate/$language/$doctype", "en")}] };
    const url = `${SITE}/en/translate/${l.slug}/${d.slug}`;
    const price = l.priceFromTh + d.surcharge;
    const ratingKey = `en-translate/${l.slug}/${d.slug}`;
    const title = `${d.nameEn} → ${l.nameEn} Certified Translation — from THB ${price.toLocaleString()} | NYC`;
    const desc = `Certified ${d.nameEn} translation into ${l.nameEn} by Ministry of Justice sworn translators. Full MFA + Apostille/embassy chain in one engagement, ${l.turnaround}. Use for ${d.destinationUses.slice(0, 2).join(", ")}. Free consultation, fast reply.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/translate/$language/$doctype", "en") },
        { name: "description", content: ctaDesc(desc, "/en/translate/$language/$doctype", "en")},
        { name: "keywords", content: `${d.nameEn} translation ${l.nameEn}, certified ${d.nameEn} translation Bangkok, Thai ${d.nameEn} to ${l.nameEn}, MFA legalization ${d.nameEn}` },
        { property: "og:title", content: ctaTitle(title, "/en/translate/$language/$doctype", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/translate/$language/$doctype", "en")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
        { property: "og:image", content: dynamicOgImage({ title: `${d.nameEn} → ${l.nameEn}`, subtitle: `Certified · MFA · Apostille — from THB ${price.toLocaleString()}`, badge: `${l.nameEn.toUpperCase()} TRANSLATION` }) },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/translate/${l.slug}/${d.slug}`, `/en/translate/${l.slug}/${d.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `Certified ${d.nameEn} translation into ${l.nameEn}`,
              serviceType: `Certified translation of ${d.nameEn} into ${l.nameEn}`,
              areaServed: { "@type": "Country", name: "Thailand" },
              availableLanguage: [
                { "@type": "Language", name: "Thai", iso6391: "th" },
                { "@type": "Language", name: l.nameEn, alternateName: l.nativeName, iso6391: l.iso639 },
              ],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999" },
              offers: {
                "@type": "Offer", price, priceCurrency: "THB",
                priceSpecification: { "@type": "UnitPriceSpecification", price, priceCurrency: "THB", unitText: "page" },
                availability: "https://schema.org/InStock",
              },
              aggregateRating: aggregateRatingNode(ratingKey),
            }),
          },
          ...reviewNodes(ratingKey, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              "@id": `${url}#howto`,
              name: `How to order certified ${d.nameEn} → ${l.nameEn} translation`,
              totalTime: "PT30M",
              estimatedCost: { "@type": "MonetaryAmount", currency: "THB", value: price },
              step: [
                { "@type": "HowToStep", position: 1, name: "Send document via LINE", text: `Scan or photograph the ${d.nameEn} clearly and send it to LINE @nycli. We reply within 15 minutes during business hours.` },
                { "@type": "HowToStep", position: 2, name: "Receive firm quote", text: `From THB ${price.toLocaleString()}/page (typical size ${d.pagesEstimate}). Quote is firm and includes courier nationwide.` },
                { "@type": "HowToStep", position: 3, name: "Pay 50% deposit", text: "Bank transfer or PromptPay, send slip back over LINE." },
                { "@type": "HowToStep", position: 4, name: "Translation + QA", text: `Translation by Ministry of Justice sworn ${l.nameEn} translator, second-translator QA review.` },
                { "@type": "HowToStep", position: 5, name: "Certification chain", text: `Authentication chain: ${d.certifications.join(" → ")}.` },
                { "@type": "HowToStep", position: 6, name: "Delivery", text: `PDF delivered in ${l.turnaround}, originals couriered nationwide or internationally.` },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: [
                { q: `How much does ${d.nameEn} translation into ${l.nameEn} cost?`, a: `From THB ${price.toLocaleString()} per A4 page. Typical document size is ${d.pagesEstimate}. Notary +THB 1,500/document, MFA +THB 800-1,200/stamp, embassy legalization +THB 1,500-5,500 (only if destination is non-Hague).` },
                { q: `Will the destination authority accept this translation?`, a: `Yes. The full chain — ${d.certifications.join(" → ")} — is the standard accepted by ${d.destinationUses.slice(0, 2).join(" and ")}. We have processed thousands of files for these exact use cases.` },
                { q: `How long does it take?`, a: `${l.turnaround} for translation, plus 2-3 business days for MFA legalization. Total typical timeline including courier is 5-10 business days. 24-hour rush available.` },
                { q: `Do I need to send the original document?`, a: `For MFA legalization, yes — MFA requires inspection of the original certified Thai document. We can collect it from anywhere in Thailand and return it with the legalized package.` },
              ].map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Translation", item: `${SITE}/en/translate` },
                { "@type": "ListItem", position: 3, name: `${l.nameEn} translation`, item: `${SITE}/en/translate/${l.slug}` },
                { "@type": "ListItem", position: 4, name: d.nameEn, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: EnTranslatePairPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Page not found</h1>
      <Link to="/en/translate" className="mt-4 inline-block text-gold underline">Back to translation hub</Link>
    </main>
  ),
});

function EnTranslatePairPage() {
  const { lang, doc } = Route.useLoaderData() as { lang: string; doc: string };
  const l = getTranslationLanguage(lang)!;
  const d = getTranslationDocType(doc)!;
  const price = l.priceFromTh + d.surcharge;
  const sections = enPairSections(l, d);
  const otherDocs = TRANSLATION_DOCTYPES.filter((x) => x.slug !== doc).slice(0, 8);
  const otherLangs = TRANSLATION_LANGUAGES.filter((x) => x.slug !== lang).slice(0, 8);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/translate" className="hover:text-gold">Translation</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/translate/$language" params={{ language: l.slug }} className="hover:text-gold">{l.nameEn}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{d.nameEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Languages className="h-3.5 w-3.5" /> {d.nameEn} → {l.nameEn}{l.nativeName ? ` · ${l.nativeName}` : ""}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            {d.nameEn} Translation into {l.nameEn} — <span className="text-gold">Certified & Legalized</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            Certified {d.nameEn} translation into {l.nameEn} by Ministry of Justice sworn translators, packaged with the full {d.certifications.join(" → ")} chain. Accepted by {d.destinationUses.slice(0, 2).join(" and ")}.
          </p>
          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "From", v: `THB ${price.toLocaleString()}/pg` },
              { k: "Typical size", v: d.pagesEstimate },
              { k: "Turnaround", v: l.turnaround },
              { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> Send file via LINE
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC Language Institute (DBD 0435567000061, since 2010) translates {d.nameEn} ({d.nameTh}) into {l.nameEn} for THB {price.toLocaleString()} per page, delivered in {l.turnaround}. Certification chain: {d.certifications.join(" → ")}. Accepted by {d.destinationUses.slice(0, 3).join(", ")}. 4.9/5 across 2,147 verified reviews.
        </blockquote>

        {sections.map((s) => (
          <section key={s.heading} className="mt-12">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{s.heading}</h2>
            {s.paragraphs.map((p, i) => (
              <p key={i} className="mt-4 text-[15px] leading-7 text-slate-700">{p}</p>
            ))}
            {s.bullets && (
              <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-sm text-slate-800">
                    <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {b}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Where the translation is accepted</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {l.countryOfUse.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-navy-deep/15 bg-white px-3.5 py-1.5 text-[13px] font-semibold text-navy-deep">
                <Globe2 className="h-3.5 w-3.5 text-amber-600" /> {c}
              </span>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Starting price</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            {d.nameEn} → {l.nameEn} from THB {price.toLocaleString()}/page
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Corporate seal + MoJ-listed sworn translator signature on every page</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Notary Public certification available (+THB 1,500/document)</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> MFA legalization (Chaeng Watthana) handled end-to-end</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> EMS / DHL / FedEx return nationwide and worldwide</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Frequently asked questions</h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion
              items={[
                { q: `How much is ${d.nameEn} → ${l.nameEn}?`, a: `From THB ${price.toLocaleString()} per A4 page. Typical size: ${d.pagesEstimate}.` },
                { q: `What is the full certification chain?`, a: d.certifications.join(" → ") },
                { q: `Where will this be accepted?`, a: d.destinationUses.join(" · ") },
                { q: `How fast can it be done?`, a: `Standard ${l.turnaround} from confirmed deposit. 24-hour rush available (+50%) up to 5 pages.` },
              ]}
            />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other documents we translate into {l.nameEn}</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {otherDocs.map((o) => (
              <Link
                key={o.slug}
                to="/en/translate/$language/$doctype"
                params={{ language: l.slug, doctype: o.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{o.category}</div>
                <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                  {o.nameEn}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">From THB {(l.priceFromTh + o.surcharge).toLocaleString()} · {o.pagesEstimate}</div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Same document in other languages</h2>
          <div className="mt-5 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
            {otherLangs.map((o) => (
              <Link
                key={o.slug}
                to="/en/translate/$language/$doctype"
                params={{ language: o.slug, doctype: d.slug }}
                className="group rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 transition hover:border-gold/50"
              >
                <div className="font-semibold group-hover:text-amber-700">{d.nameEn} → {o.nameEn}</div>
                <div className="text-[11px] text-slate-500">From THB {(o.priceFromTh + d.surcharge).toLocaleString()}</div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
